// types
import { Ifr, IfrTables, Project } from '../@types';

// i18n
import { useTranslation } from 'react-i18next';

// styles
import { printIfrStyles } from '../styles/print.ifr.styles';

// utils
import { formatDate } from '../utils/dates';
import { formatNumber } from '../utils/format';
import { getIfrTableTitle, sanitizeIfrTableRowsForExport, splitWideTable } from '../utils/ifrs';

// props
export interface HandlePrintIfrParams {
	ifr: Ifr;
	ifrTables: IfrTables;
	project: null | Project;
}

export interface GeneratePrintContentParams {
	ifr: Ifr;
	ifrTables: IfrTables;
	project: null | Project;
}

export const useIfrPrint = () => {
	// hooks
	const { t } = useTranslation();

	const generatePrintContent = ({
		ifr,
		ifrTables,
		project,
	}: GeneratePrintContentParams): string => {
		// Number formatting options for consistent display
		const numberFormatOptions = {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		};

		// Generate HTML for each table with splitting for wide tables
		const tablesHtml = Object.entries(ifrTables)
			.flatMap(([tableKey, table], index) => {
				if (!table.rows || table.rows.length === 0) return [];

				const title = getIfrTableTitle({ key: tableKey, t });

				// Sanitize rows using the existing utility function
				const sanitizedRows = sanitizeIfrTableRowsForExport({
					rows: table.rows,
					key: tableKey,
				});

				if (sanitizedRows.length === 0) return [];

				// Split wide tables if they exceed maximum columns
				const splitTables = splitWideTable({
					table: {
						columns: table.columns,
						rows: sanitizedRows,
					},
					title,
					maxCols: 12,
					hasFirstColInSplit: true,
				});

				// Generate HTML for each split table
				return splitTables.map((splitTable) => {
					const classTablePart = splitTable.isPartTable ? ' table-part' : '';

					return `
					<div class="table-section${classTablePart}">
						<h3>${splitTable.title}</h3>
						<table>
							<thead>
								<tr>
									${splitTable.columns
										.map((column) => {
											let classTh = 'col-data';

											if (column.field === 'name') {
												classTh = 'col-label';
											} else if (column.field.includes('total')) {
												classTh = 'col-summary';
											}

											return `<th class="${classTh}">${column.headerName || column.field}</th>`;
										})
										.join('')}
								</tr>
							</thead>
							<tbody>
								${splitTable.rows
									.map((row) => {
										const isTotal = row.name === t('total') || String(row.id).includes('total');
										const classRow = isTotal ? 'row-summary' : 'data-row';

										return `
										<tr class="${classRow}">
											${splitTable.columns
												.map((column) => {
													const value = row[column.field];
													const classTd =
														column.field === 'name'
															? 'col-label'
															: column.field.includes('total')
															? 'col-summary'
															: 'col-data';

													let displayValue = '';
													if (value === null || value === undefined || value === '') {
														displayValue = '';
													} else if (typeof value === 'number') {
														displayValue = formatNumber(value, numberFormatOptions);
													} else if (typeof value === 'string' && value.includes('%')) {
														displayValue = value; // Keep percentage as-is
													} else {
														displayValue = String(value);
													}

													return `<td class="${classTd}">${displayValue}</td>`;
												})
												.join('')}
										</tr>
									`;
									})
									.join('')}
							</tbody>
						</table>
					</div>
					`;
				});
			})
			.join('');

		// Format date range for display
		const formatDateRange = () => {
			if (ifr?.dateStart && ifr?.dateEnd) {
				const startDateFormatted = formatDate({
					date: new Date(ifr.dateStart),
					format: 'YYYY-MM-DD',
				});
				const endDateFormatted = formatDate({
					date: new Date(ifr.dateEnd),
					format: 'YYYY-MM-DD',
				});

				return `${startDateFormatted} - ${endDateFormatted}`;
			}
			return '';
		};

		return `
			<div class="page">
				<div class="print-header">
					<h1>${ifr?.name || 'IFR Report'}</h1>
					<div class="print-subtitle">
						<span><strong>Project:</strong> ${project?.name || ''}</span>
						<span><strong>Currency:</strong> ${ifr?.currency || ''}</span>
						${formatDateRange() ? `<span><strong>Period:</strong> ${formatDateRange()}</span>` : ''}
						<span><strong>Status:</strong> ${ifr?.status || ''}</span>
					</div>
				</div>
				
				${tablesHtml}
			</div>
		`;
	};

	const handlePrintIfr = ({ ifr, ifrTables, project }: HandlePrintIfrParams) => {
		// Open a new window for printing
		const printWindow = window.open('', '_blank');
		if (!printWindow) return;

		// Generate the print content
		const printContent = generatePrintContent({ ifr, ifrTables, project });
		const dateToday = formatDate({
			date: new Date(),
			format: 'YYYY-MM-DD',
		});
		const documentTitle = `${project?.idWbg}-ifr-${dateToday}`;
		const doc = printWindow.document;

		// Create and set the HTML structure
		doc.head.innerHTML = `
			<title>${documentTitle}</title>
			<style>
				${printIfrStyles}
			</style>
		`;

		doc.body.innerHTML = printContent;

		// Use requestAnimationFrame to ensure DOM is rendered before printing
		printWindow.requestAnimationFrame(() => {
			printWindow.focus();
			printWindow.print();
			printWindow.close();
		});
	};

	return {
		handlePrintIfr,
	};
};
